import React, { useCallback, useEffect, useRef, useState } from 'react';
import PhoneInput, { CountryData } from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';

interface PhoneNumberInputProps {
  setPhoneNumber: React.Dispatch<React.SetStateAction<string | undefined>>;
  setCountryPhoneCode: React.Dispatch<React.SetStateAction<string | undefined>>;
  initialPhoneNumber?: string;
  initialCountryCode?: string;
  disabled?: boolean;
  onBlur?: () => void;
}

export function PhoneNumberInput({
  setPhoneNumber,
  setCountryPhoneCode,
  initialPhoneNumber,
  initialCountryCode,
  disabled,
  onBlur
}: Readonly<PhoneNumberInputProps>) {
  const [phone, setPhone] = useState<string>('+60');
  const [countryCode, setCountryCode] = useState<string>('my');
  // Prevent re-initialization when parent live state flows back through initialPhoneNumber.
  const initialized = useRef(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (initialized.current) return;
    if (!initialPhoneNumber && !initialCountryCode) {
      return;
    }
    initialized.current = true;

    const normalizedCountryCode = (initialCountryCode || '').replace('+', '');
    const normalizedPhoneValue = initialPhoneNumber
      ? initialPhoneNumber.startsWith('+')
        ? initialPhoneNumber
        : `+${initialPhoneNumber}`
      : normalizedCountryCode
        ? `+${normalizedCountryCode}`
        : '+60';

    setPhone(normalizedPhoneValue);

    if (normalizedCountryCode) {
      setCountryPhoneCode(normalizedCountryCode);
      const rawNumber = normalizedPhoneValue.replace(
        `+${normalizedCountryCode}`,
        ''
      );
      const cleanedNumber = rawNumber.replace(/^0+/, '');
      setPhoneNumber(cleanedNumber);
    }
  }, [
    initialCountryCode,
    initialPhoneNumber,
    setCountryPhoneCode,
    setPhoneNumber
  ]);

  const handlePhoneChange = useCallback(
    (value: string, data: CountryData) => {
      const normalizedValue = value.startsWith('+') ? value : `+${value}`;
      const dialCode = String(data?.dialCode || '');

      setPhone(normalizedValue);
      setCountryPhoneCode(dialCode);
      if (data?.countryCode) {
        setCountryCode(data.countryCode);
      }

      const rawNumber = normalizedValue.replace(`+${dialCode}`, '');
      const cleanedNumber = rawNumber.replace(/^0+/, '');
      setPhoneNumber(cleanedNumber);

      // After country selection (no subscriber digits yet), focus the number input.
      if (!cleanedNumber) {
        setTimeout(() => {
          containerRef.current?.querySelector('input')?.focus();
        }, 0);
      }
    },
    [setPhoneNumber, setCountryPhoneCode]
  );

  return (
    <div ref={containerRef} style={styles.container}>
      <PhoneInput
        country={countryCode}
        value={phone}
        onChange={handlePhoneChange}
        disabled={disabled}
        inputStyle={{
          ...styles.input,
          ...(disabled
            ? {
                backgroundColor: '#f3f4f6',
                color: '#6b7280',
                cursor: 'not-allowed'
              }
            : {})
        }}
        buttonStyle={{
          ...styles.flagButton,
          ...(disabled
            ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' }
            : {})
        }}
        enableSearch
        disableSearchIcon={false}
        disableCountryCode={false}
        countryCodeEditable
        inputProps={{
          name: 'phone',
          required: true,
          autoFocus: false,
          onBlur
        }}
      />
    </div>
  );
}

const styles = {
  container: {
    width: '100%',
    margin: '8px 0',
    display: 'flex',
    justifyContent: 'center'
  },
  input: {
    width: '100%',
    height: '40px',
    fontSize: '16px',
    borderRadius: '8px'
  },
  flagButton: {
    width: '50px',
    borderRadius: '8px 0 0 8px'
  }
};
